import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Button } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import type HlsJs from 'hls.js';
import { CompactStat, CompactStatGroup } from '../components/CompactStat';
import styles from './HlsPreviewPlayer.module.css';

type HlsLevel = NonNullable<HlsJs['levels']>[number];

/** Snapshot of playback state for the stats panel. */
export interface PlaybackStats {
  playState: string;
  currentTime: number;
  duration: number;
  levelIdx: number;
  levels: HlsLevel[];
  bitrate: number;
  width: number;
  height: number;
  codec: string;
  bandwidthEst: number;
  bufferAhead: number;
  droppedFrames: number;
  totalFrames: number;
  /** Seconds the playhead trails the live edge (0 for VOD). */
  liveLatency: number;
}

/**
 * Stats hls.js can't read from the playlist and must observe at runtime. A
 * single-rendition MEDIA playlist carries no BANDWIDTH/CODECS tags — bitrate is
 * measured from loaded segments and codecs come from the BUFFER_CODECS event.
 */
interface DynStats {
  videoCodec: string;
  audioCodec: string;
  bitrate: number; // bits/sec, observed from segment sizes
}

/** A level's display label — height ("720p") preferred, else bitrate. */
function levelLabel(lvl: HlsLevel): string {
  if (lvl.height) return `${lvl.height}p`;
  if (lvl.bitrate) return `${Math.round(lvl.bitrate / 1000)}k`;
  return 'Source';
}

/** Compute the buffer-ahead (seconds) at the current playhead. */
function bufferAhead(video: HTMLVideoElement): number {
  const b = video.buffered;
  for (let i = 0; i < b.length; i++) {
    if (video.currentTime >= b.start(i) && video.currentTime <= b.end(i)) {
      return b.end(i) - video.currentTime;
    }
  }
  return 0;
}

/**
 * Seconds the playhead trails the live edge. Prefers hls.js's own `latency`
 * (edge minus playhead); falls back to the seekable range end for native HLS.
 * 0 when not a live stream.
 */
function liveLatency(hls: HlsJs | null, video: HTMLVideoElement): number {
  const l = hls?.latency;
  if (typeof l === 'number' && l > 0) return l;
  const sk = video.seekable;
  if (sk.length) return Math.max(0, sk.end(sk.length - 1) - video.currentTime);
  return 0;
}

/** Snapshot hls.js + video element state for the stats panel. */
function computeStats(hls: HlsJs | null, video: HTMLVideoElement, dyn: DynStats): PlaybackStats {
  const lvl = (hls?.levels && hls.levels[hls.currentLevel]) || undefined;
  let dropped = 0;
  let total = 0;
  if (video.getVideoPlaybackQuality) {
    const q = video.getVideoPlaybackQuality();
    dropped = q.droppedVideoFrames || 0;
    total = q.totalVideoFrames || 0;
  }
  const codecFromLevel = lvl?.codecSet || [lvl?.videoCodec, lvl?.audioCodec].filter(Boolean).join(', ');
  const codecFromDyn = [dyn.videoCodec, dyn.audioCodec].filter(Boolean).join(', ');
  return {
    playState: video.paused ? 'paused' : video.ended ? 'ended' : 'playing',
    currentTime: video.currentTime,
    duration: Number.isFinite(video.duration) ? video.duration : 0,
    levelIdx: hls?.currentLevel ?? -1,
    levels: hls?.levels ?? [],
    bitrate: lvl?.bitrate || dyn.bitrate || 0,
    width: lvl?.width || video.videoWidth || 0,
    height: lvl?.height || video.videoHeight || 0,
    codec: codecFromLevel || codecFromDyn || '',
    bandwidthEst: hls?.bandwidthEstimate || 0,
    bufferAhead: bufferAhead(video),
    droppedFrames: dropped,
    totalFrames: total,
    liveLatency: liveLatency(hls, video),
  };
}

function fmtClock(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${h ? `${h}:` : ''}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Format a bits/sec rate adaptively: Mbps when large, else kbps. */
function fmtRate(bps: number): string {
  if (!bps || bps <= 0) return '—';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}

/**
 * HLS player — native where supported (iOS Safari), hls.js elsewhere. Keeps the
 * hls.js instance, polls playback stats (~2×/s) into `onStats`, and exposes the
 * instance via `onReady` so the parent can switch quality levels.
 *
 * `live` pins the playhead to the live edge and blocks scrubbing — for linear
 * streams with no rewind. VOD (live=false) allows normal seeking.
 */
function HlsPlayer({
  src,
  live,
  onStats,
  onReady,
  onFatalError,
  xhrSetup,
}: {
  src: string;
  live: boolean;
  onStats: (s: PlaybackStats | null) => void;
  onReady: (hls: HlsJs | null) => void;
  /** Unrecoverable playback failure (manifest 404, decode error…). */
  onFatalError: (message: string) => void;
  /** Configure every hls.js XHR (e.g. attach an auth header for a proxied source). */
  xhrSetup?: (xhr: XMLHttpRequest, url: string) => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return undefined;

    let cancelled = false;
    let instance: HlsJs | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;

    const dyn: DynStats = { videoCodec: '', audioCodec: '', bitrate: 0 };

    const startPolling = (hls: HlsJs | null) => {
      timer = setInterval(() => onStats(computeStats(hls, video, dyn)), 500);
    };

    // Live: pin to the live edge and snap back on any seek. The 1.5s tolerance
    // avoids fighting hls.js's own small gap-jump nudges.
    const liveEdge = (): number | null => {
      const pos = instance?.liveSyncPosition;
      if (typeof pos === 'number' && Number.isFinite(pos)) return pos;
      const sk = video.seekable;
      return sk.length ? sk.end(sk.length - 1) : null;
    };
    const onSeeking = () => {
      if (!live) return;
      const edge = liveEdge();
      if (edge != null && Math.abs(video.currentTime - edge) > 1.5) {
        video.currentTime = edge;
      }
    };
    video.addEventListener('seeking', onSeeking);

    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return;
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          // Attach caller-supplied request config (e.g. Bearer auth for the
          // @mgmt preview proxy) to every manifest/segment XHR.
          ...(xhrSetup && { xhrSetup }),
          // Tolerate sub-second PTS gaps at program boundaries instead of
          // throwing bufferAppendError.
          maxBufferHole: 1,
          // Live linear: hug the edge instead of buffering minutes ahead like
          // VOD. Sit a few segments back for stability, gently speed up to
          // recover the edge after a stall, and keep no rewind buffer (the
          // scrubber is hidden anyway), so the player reads as live, not VOD.
          ...(live && {
            liveSyncDurationCount: 3,
            liveMaxLatencyDurationCount: 6,
            maxLiveSyncPlaybackRate: 1.5,
            backBufferLength: 0,
          }),
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          void video.play().catch(() => {});
        });
        // Codecs aren't always in the playlist — read them once MSE buffers exist.
        hls.on(Hls.Events.BUFFER_CODECS, (_e, data) => {
          if (data.video?.codec) dyn.videoCodec = data.video.codec;
          if (data.audio?.codec) dyn.audioCodec = data.audio.codec;
        });
        // Measure bitrate from each loaded segment (bytes·8 / duration), smoothed.
        hls.on(Hls.Events.FRAG_LOADED, (_e, data) => {
          const bytes = data.frag?.stats?.total ?? 0;
          const dur = data.frag?.duration ?? 0;
          if (bytes > 0 && dur > 0) {
            const inst = (bytes * 8) / dur;
            dyn.bitrate = dyn.bitrate ? dyn.bitrate * 0.7 + inst * 0.3 : inst;
          }
        });
        // Surface unrecoverable failures (manifest 404, decode errors) instead
        // of leaving the player on a silent spinner.
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          const detail = data.details || data.type || 'playback error';
          onFatalError(
            data.type === 'networkError'
              ? `The stream could not be loaded (${detail}).`
              : `Playback failed (${detail}).`,
          );
        });
        instance = hls;
        onReady(hls);
        startPolling(hls);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // iOS Safari: no MSE → native HLS. Element-level stats still work.
        video.src = src;
        video.addEventListener(
          'error',
          () => onFatalError('The stream could not be loaded.'),
          { once: true },
        );
        void video.play().catch(() => {});
        onReady(null);
        startPolling(null);
      } else {
        onReady(null);
        onFatalError('HLS playback is not supported in this browser.');
      }
    });

    return () => {
      cancelled = true;
      video.removeEventListener('seeking', onSeeking);
      if (timer) clearInterval(timer);
      instance?.destroy();
      onReady(null);
      onStats(null);
    };
  }, [src, live, onStats, onReady, onFatalError, xhrSetup]);

  // controlsList hides download/pip; live seeking is blocked above.
  return (
    <div className={live ? `${styles.player} ${styles.playerLive}` : styles.player}>
      <video ref={ref} controls playsInline muted controlsList="nodownload noplaybackrate" />
    </div>
  );
}

/** Playback stats + quality-preset switcher. */
function StatsPanel({
  stats,
  live,
  onPickLevel,
  error,
}: {
  stats: PlaybackStats | null;
  live: boolean;
  onPickLevel: (idx: number) => void;
  /** Fatal playback error — shown instead of the "waiting" placeholder. */
  error?: string | null;
}) {
  if (!stats) {
    return (
      <div className={styles.waiting}>
        {error ? 'Playback failed — no stats available.' : 'Waiting for the player…'}
      </div>
    );
  }
  const res = stats.width && stats.height ? `${stats.width}×${stats.height}` : '—';
  return (
    <div className={styles.stack}>
      <CompactStatGroup columns={2}>
        <CompactStat
          label={live ? 'Stream' : 'State'}
          value={live ? 'LIVE' : stats.playState}
          dotColor={live ? '#ef4444' : undefined}
        />
        {live ? (
          // Linear stream: there's no fixed duration to scrub against — show how
          // far the playhead trails the live edge instead of a VOD position bar.
          <CompactStat label="Latency" value={`${stats.liveLatency.toFixed(1)} s`} hint="behind live" />
        ) : (
          <CompactStat
            label="Position"
            value={fmtClock(stats.currentTime)}
            hint={stats.duration ? `of ${fmtClock(stats.duration)}` : undefined}
          />
        )}
        <CompactStat label="Level" value={stats.levelIdx >= 0 ? `#${stats.levelIdx}` : 'auto'} hint={res} />
        <CompactStat label="Bitrate" value={fmtRate(stats.bitrate)} />
        <CompactStat label="Bandwidth est." value={fmtRate(stats.bandwidthEst)} hint="network" />
        <CompactStat label="Buffer ahead" value={`${stats.bufferAhead.toFixed(1)} s`} />
        <CompactStat
          label="Dropped frames"
          value={stats.droppedFrames.toLocaleString()}
          hint={stats.totalFrames ? `of ${stats.totalFrames.toLocaleString()}` : undefined}
        />
        <CompactStat label="Codec" value={stats.codec || '—'} />
      </CompactStatGroup>

      {stats.levels.length > 0 && (
        <div className={styles.stack}>
          <span className={styles.statsLabel}>Quality</span>
          <div className={styles.qualityRow}>
            <Button size="sm" variant={stats.levelIdx === -1 ? 'primary' : 'secondary'} onClick={() => onPickLevel(-1)}>
              Auto
            </Button>
            {stats.levels.map((lvl, idx) => (
              <Button
                key={idx}
                size="sm"
                variant={stats.levelIdx === idx ? 'primary' : 'secondary'}
                onClick={() => onPickLevel(idx)}
              >
                {levelLabel(lvl)}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Shared HLS preview: a player card (left) + live playback-stats card (right).
 * Used by FastChannel channel preview and the DRM stream-detail preview.
 *
 * - `src` empty → renders the empty placeholder (stream not provisioned yet).
 * - `live` pins the player to the live edge and hides the scrubber (linear).
 * - `banner` renders above the video (e.g. FastChannel's "now playing" strip).
 */
export function HlsPreviewPlayer({
  src,
  title,
  description,
  live = false,
  banner,
  emptyMessage = 'No stream available yet.',
  statsDescription = 'Live hls.js metrics for the stream.',
  onLatency,
  xhrSetup,
  copyUrl,
}: {
  src: string;
  title: ReactNode;
  description?: ReactNode;
  live?: boolean;
  banner?: ReactNode;
  emptyMessage?: ReactNode;
  statsDescription?: ReactNode;
  /** Live latency (s) the playhead trails the edge — lets the parent align a
   *  "now playing" banner to what the player is actually showing. */
  onLatency?: (seconds: number) => void;
  /** Configure every hls.js XHR (e.g. attach a Bearer token when `src` points at
   *  the authenticated @mgmt preview proxy). */
  xhrSetup?: (xhr: XMLHttpRequest, url: string) => void;
  /** URL shown in the copy/open field, when the playback `src` isn't what the
   *  user should see (e.g. `src` is an internal proxy URL). Defaults to `src`. */
  copyUrl?: string;
}) {
  const { show } = useToast();
  const [stats, setStats] = useState<PlaybackStats | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const hlsRef = useRef<HlsJs | null>(null);

  // A new source gets a fresh chance before we declare playback dead.
  useEffect(() => {
    setFatalError(null);
  }, [src]);

  useEffect(() => {
    if (onLatency) onLatency(stats?.liveLatency ?? 0);
  }, [stats, onLatency]);

  const onReady = useCallback((hls: HlsJs | null) => {
    hlsRef.current = hls;
  }, []);
  const onPickLevel = useCallback((idx: number) => {
    if (hlsRef.current) hlsRef.current.currentLevel = idx;
  }, []);

  // What the user sees/copies — the public URL, not an internal proxy `src`.
  const displayUrl = copyUrl ?? src;

  const copy = () => {
    void navigator.clipboard?.writeText(displayUrl);
    show({ tone: 'success', title: 'Stream URL copied' });
  };

  return (
    <div className={styles.previewGrid}>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardBody>
          <div className={styles.stack}>
            {banner}
            {!src ? (
              <div className={styles.playerEmpty}>{emptyMessage}</div>
            ) : fatalError ? (
              <div className={styles.playerEmpty}>{fatalError}</div>
            ) : (
              <HlsPlayer src={src} live={live} onStats={setStats} onReady={onReady} onFatalError={setFatalError} xhrSetup={xhrSetup} />
            )}

            {src && (
              <div className={styles.copyField}>
                <span className={styles.copyFieldText}>{displayUrl}</span>
                <Button variant="ghost" size="sm" startIcon={<Copy size={14} />} onClick={copy}>
                  Copy
                </Button>
                {/* A relative path (proxied preview) can't be opened standalone —
                    resolving it against the app origin 404s, and the auth proxy
                    URL needs a Bearer header a plain navigation can't send. */}
                {/^https?:\/\//i.test(displayUrl) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    startIcon={<ExternalLink size={14} />}
                    onClick={() => window.open(displayUrl, '_blank', 'noopener')}
                  >
                    Open
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Playback stats</CardTitle>
          <CardDescription>{statsDescription}</CardDescription>
        </CardHeader>
        <CardBody>
          <StatsPanel stats={stats} live={live} onPickLevel={onPickLevel} error={fatalError} />
        </CardBody>
      </Card>
    </div>
  );
}

export default HlsPreviewPlayer;
